import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Store,
  ShoppingCart,
  Megaphone,
  BarChart3,
  Calculator,
  Search,
  CheckCircle2,
  Link2,
  Zap,
  Database,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { ConnectionCard } from './ConnectionCard';
import { PropertySelectModal, SelectedProperty } from './PropertySelectModal';
import { GoogleServicesSetupModal } from './GoogleServicesSetupModal';
import { PLATFORMS, PLATFORM_CATEGORIES, getPlatformsByCategory } from '../config/platforms.config';
import { Platform, PlatformCategory, Connection } from '../types';
import {
  isGooglePlatform,
  redirectToGoogleAuth,
  getGoogleConnections,
  disconnectGoogleAccount,
  getSelectedProperties,
  GoogleConnection,
  SelectedProperties,
} from '../services/googleOAuthService';
import { TrendyolCredentials } from '../services/trendyolApiService';
import { TrendyolConnectModal } from './TrendyolConnectModal';
import { TrendyolSettingsModal, TrendyolSettings } from './TrendyolSettingsModal';
import { HepsiburadaCredentials } from '../services/hepsiburadaApiService';
import { HepsiburadaConnectModal } from './HepsiburadaConnectModal';
import { CiceksepetiCredentials, CiceksepetiConnectModal } from './CiceksepetiConnectModal';
import { PttAVMCredentials, PttAVMConnectModal } from './PttAVMConnectModal';
import { N11Credentials, N11ConnectModal } from './N11ConnectModal';
import { BeymenCredentials, BeymenConnectModal } from './BeymenConnectModal';
import { useAuth } from '@/context/AuthContext';

// Icon mapping for categories
const categoryIcons: Record<PlatformCategory, typeof Store> = {
  marketplace: Store,
  ecommerce: ShoppingCart,
  advertising: Megaphone,
  analytics: BarChart3,
  accounting: Calculator,
};

// Mock connections for demo - empty by default to show connect button
const mockConnections: Connection[] = [];

export function ConnectionsPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [selectedCategory, setSelectedCategory] = useState<PlatformCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [connections, setConnections] = useState<Connection[]>(mockConnections);
  const [googleConnections, setGoogleConnections] = useState<GoogleConnection[]>([]);
  const [googleSelectedProperties, setGoogleSelectedProperties] = useState<Record<string, SelectedProperties | null>>({});
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingTrendyol, setIsLoadingTrendyol] = useState(false);
  const [isLoadingHepsiburada, setIsLoadingHepsiburada] = useState(false);
  const [isLoadingCiceksepeti, setIsLoadingCiceksepeti] = useState(false);
  const [isLoadingPttavm, setIsLoadingPttavm] = useState(false);
  const [isLoadingN11, setIsLoadingN11] = useState(false);
  const [isLoadingBeymen, setIsLoadingBeymen] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthSuccess, setOauthSuccess] = useState<string | null>(null);

  // Property selection modal state
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [selectedGoogleConnection, setSelectedGoogleConnection] = useState<{
    connectionId: string;
    platformId: string;
    email: string;
  } | null>(null);

  // Google Services Setup Modal state
  const [showGoogleSetupModal, setShowGoogleSetupModal] = useState(false);
  const [pendingGoogleConnection, setPendingGoogleConnection] = useState<{
    connectionId: string;
    email: string;
  } | null>(null);

  // Handle OAuth callback from URL params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const platform = searchParams.get('platform');

    if (success === 'true' && platform) {
      setOauthSuccess(isEnglish ? `Successfully connected to ${platform}!` : `${platform} başarıyla bağlandı!`);
      // Clear URL params
      setSearchParams({});
      // Reload Google connections and show setup modal
      if (user?.uid) {
        loadGoogleConnectionsAndShowSetup(user.uid);
      }
      setTimeout(() => {
        setOauthSuccess(null);
      }, 3000);
    }

    if (error) {
      // Show actual error message for debugging
      const errorMessage = decodeURIComponent(error);
      console.error('OAuth Error:', errorMessage);
      setOauthError(`${isEnglish ? 'Error' : 'Hata'}: ${errorMessage}`);
      setSearchParams({});
      setTimeout(() => {
        setOauthError(null);
      }, 10000); // Show error for 10 seconds
    }
  }, [searchParams, user, isEnglish]);

  // Load Google connections on mount
  useEffect(() => {
    if (user?.uid) {
      loadGoogleConnections(user.uid);
      loadTrendyolConnections(user.uid);
      loadHepsiburadaConnections(user.uid);
      loadCiceksepetiConnections(user.uid);
      loadPttavmConnections(user.uid);
      loadN11Connections(user.uid);
      loadBeymenConnections(user.uid);
    }
  }, [user]);

  const loadTrendyolConnections = async (userId: string) => {
    setIsLoadingTrendyol(true);
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/getTrendyolConnections?userId=${userId}`);
      const data = await response.json();

      if (data.connections && data.connections.length > 0) {
        const trendyolConnections: Connection[] = data.connections.map((conn: {
          id: string;
          sellerId: string;
          status: string;
          createdAt: { _seconds: number };
          updatedAt: { _seconds: number };
          lastSync?: { _seconds: number };
        }) => ({
          id: conn.id,
          platformId: 'trendyol',
          status: conn.status as 'connected' | 'disconnected' | 'syncing' | 'error',
          lastSync: conn.lastSync ? new Date(conn.lastSync._seconds * 1000) : undefined,
          syncFrequency: 3600,
          settings: {
            sellerId: conn.sellerId,
          },
          createdAt: new Date(conn.createdAt._seconds * 1000),
          updatedAt: new Date(conn.updatedAt._seconds * 1000),
        }));

        setConnections(prev => {
          // Remove any existing trendyol connections and add new ones
          const withoutTrendyol = prev.filter(c => c.platformId !== 'trendyol');
          return [...withoutTrendyol, ...trendyolConnections];
        });
      }
    } catch (error) {
      console.error('Failed to load Trendyol connections:', error);
    } finally {
      setIsLoadingTrendyol(false);
    }
  };

  const loadHepsiburadaConnections = async (userId: string) => {
    setIsLoadingHepsiburada(true);
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/getHepsiburadaConnections?userId=${userId}`);
      const data = await response.json();

      if (data.connections && data.connections.length > 0) {
        const hepsiburadaConnections: Connection[] = data.connections.map((conn: {
          id: string;
          merchantId: string;
          integratorName?: string;
          status: string;
          createdAt: { _seconds: number };
          updatedAt: { _seconds: number };
          lastSync?: { _seconds: number };
          settings?: Record<string, unknown>;
        }) => ({
          id: conn.id,
          platformId: 'hepsiburada',
          status: conn.status as 'connected' | 'disconnected' | 'syncing' | 'error',
          lastSync: conn.lastSync ? new Date(conn.lastSync._seconds * 1000) : undefined,
          syncFrequency: 3600,
          merchantId: conn.merchantId,
          integratorName: conn.integratorName,
          settings: {
            merchantId: conn.merchantId,
            ...conn.settings,
          },
          createdAt: new Date(conn.createdAt._seconds * 1000),
          updatedAt: new Date(conn.updatedAt._seconds * 1000),
        }));

        setConnections(prev => {
          // Remove any existing hepsiburada connections and add new ones
          const withoutHepsiburada = prev.filter(c => c.platformId !== 'hepsiburada');
          return [...withoutHepsiburada, ...hepsiburadaConnections];
        });
      }
    } catch (error) {
      console.error('Failed to load Hepsiburada connections:', error);
    } finally {
      setIsLoadingHepsiburada(false);
    }
  };

  const loadCiceksepetiConnections = async (userId: string) => {
    setIsLoadingCiceksepeti(true);
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/getCiceksepeti?userId=${userId}`);
      const data = await response.json();

      if (data.connections && data.connections.length > 0) {
        const ciceksepetiConnections: Connection[] = data.connections.map((conn: {
          id: string;
          status: string;
          createdAt: { _seconds: number };
          updatedAt: { _seconds: number };
          lastSync?: { _seconds: number };
          settings?: Record<string, unknown>;
        }) => ({
          id: conn.id,
          platformId: 'ciceksepeti',
          status: conn.status as 'connected' | 'disconnected' | 'syncing' | 'error',
          lastSync: conn.lastSync ? new Date(conn.lastSync._seconds * 1000) : undefined,
          syncFrequency: 3600,
          settings: conn.settings || {},
          createdAt: new Date(conn.createdAt._seconds * 1000),
          updatedAt: new Date(conn.updatedAt._seconds * 1000),
        }));

        setConnections(prev => {
          // Remove any existing ciceksepeti connections and add new ones
          const withoutCiceksepeti = prev.filter(c => c.platformId !== 'ciceksepeti');
          return [...withoutCiceksepeti, ...ciceksepetiConnections];
        });
      }
    } catch (error) {
      console.error('Failed to load Ciceksepeti connections:', error);
    } finally {
      setIsLoadingCiceksepeti(false);
    }
  };

  const loadPttavmConnections = async (userId: string) => {
    setIsLoadingPttavm(true);
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/getPttavm?userId=${userId}`);
      const data = await response.json();

      if (data.connections && data.connections.length > 0) {
        const pttavmConnections: Connection[] = data.connections.map((conn: {
          id: string;
          shopId: string;
          supplierName: string;
          status: string;
          createdAt: { _seconds: number };
          updatedAt: { _seconds: number };
          lastSync?: { _seconds: number };
          settings?: Record<string, unknown>;
        }) => ({
          id: conn.id,
          platformId: 'pttavm',
          status: conn.status as 'connected' | 'disconnected' | 'syncing' | 'error',
          lastSync: conn.lastSync ? new Date(conn.lastSync._seconds * 1000) : undefined,
          syncFrequency: 3600,
          settings: {
            shopId: conn.shopId,
            supplierName: conn.supplierName,
            ...conn.settings,
          },
          createdAt: new Date(conn.createdAt._seconds * 1000),
          updatedAt: new Date(conn.updatedAt._seconds * 1000),
        }));

        setConnections(prev => {
          // Remove any existing pttavm connections and add new ones
          const withoutPttavm = prev.filter(c => c.platformId !== 'pttavm');
          return [...withoutPttavm, ...pttavmConnections];
        });
      }
    } catch (error) {
      console.error('Failed to load PttAVM connections:', error);
    } finally {
      setIsLoadingPttavm(false);
    }
  };

  const loadN11Connections = async (userId: string) => {
    setIsLoadingN11(true);
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/getN11?userId=${userId}`);
      const data = await response.json();

      if (data.connections && data.connections.length > 0) {
        const n11Connections: Connection[] = data.connections.map((conn: {
          id: string;
          storeName: string;
          status: string;
          createdAt: { _seconds: number };
          updatedAt: { _seconds: number };
          lastSync?: { _seconds: number };
          settings?: Record<string, unknown>;
        }) => ({
          id: conn.id,
          platformId: 'n11',
          status: conn.status as 'connected' | 'disconnected' | 'syncing' | 'error',
          lastSync: conn.lastSync ? new Date(conn.lastSync._seconds * 1000) : undefined,
          syncFrequency: 3600,
          metadata: {
            storeName: conn.storeName,
          },
          settings: conn.settings || {},
          createdAt: new Date(conn.createdAt._seconds * 1000),
          updatedAt: new Date(conn.updatedAt._seconds * 1000),
        }));

        setConnections(prev => {
          // Remove any existing n11 connections and add new ones
          const withoutN11 = prev.filter(c => c.platformId !== 'n11');
          return [...withoutN11, ...n11Connections];
        });
      }
    } catch (error) {
      console.error('Failed to load N11 connections:', error);
    } finally {
      setIsLoadingN11(false);
    }
  };

  const loadBeymenConnections = async (userId: string) => {
    setIsLoadingBeymen(true);
    try {
      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
      const response = await fetch(`${FUNCTIONS_URL}/getBeymen?userId=${userId}`);
      const data = await response.json();

      if (data.connections && data.connections.length > 0) {
        const beymenConnections: Connection[] = data.connections.map((conn: {
          id: string;
          storeName: string;
          status: string;
          createdAt: { _seconds: number };
          updatedAt: { _seconds: number };
          lastSync?: { _seconds: number };
          settings?: Record<string, unknown>;
        }) => ({
          id: conn.id,
          platformId: 'beymen',
          status: conn.status as 'connected' | 'disconnected' | 'syncing' | 'error',
          lastSync: conn.lastSync ? new Date(conn.lastSync._seconds * 1000) : undefined,
          syncFrequency: 3600,
          metadata: {
            storeName: conn.storeName,
          },
          settings: conn.settings || {},
          createdAt: new Date(conn.createdAt._seconds * 1000),
          updatedAt: new Date(conn.updatedAt._seconds * 1000),
        }));

        setConnections(prev => {
          // Remove any existing beymen connections and add new ones
          const withoutBeymen = prev.filter(c => c.platformId !== 'beymen');
          return [...withoutBeymen, ...beymenConnections];
        });
      }
    } catch (error) {
      console.error('Failed to load Beymen connections:', error);
    } finally {
      setIsLoadingBeymen(false);
    }
  };

  const loadGoogleConnections = async (userId: string) => {
    setIsLoadingGoogle(true);
    try {
      const googleConns = await getGoogleConnections(userId);
      setGoogleConnections(googleConns);

      // Also add to main connections state for UI display
      if (googleConns && googleConns.length > 0) {
        const mappedConnections: Connection[] = googleConns.map((conn) => ({
          id: conn.id,
          platformId: 'google',
          status: 'connected' as const,
          lastSync: new Date(),
          syncFrequency: 3600,
          settings: {
            googleEmail: conn.googleEmail,
            googleName: conn.googleName,
            googlePicture: conn.googlePicture,
          },
          createdAt: conn.createdAt,
          updatedAt: conn.updatedAt,
        }));

        setConnections(prev => {
          const withoutGoogle = prev.filter(c => c.platformId !== 'google');
          return [...withoutGoogle, ...mappedConnections];
        });

        // Load selected properties for each connection
        const propertiesMap: Record<string, SelectedProperties | null> = {};
        for (const conn of googleConns) {
          try {
            const props = await getSelectedProperties(userId, conn.id);
            propertiesMap[conn.id] = props;
          } catch (e) {
            console.error('Failed to load properties for connection:', conn.id, e);
            propertiesMap[conn.id] = null;
          }
        }
        setGoogleSelectedProperties(propertiesMap);
      }
    } catch (error) {
      console.error('Failed to load Google connections:', error);
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  // Load Google connections and show setup modal for the newest one
  const loadGoogleConnectionsAndShowSetup = async (userId: string) => {
    setIsLoadingGoogle(true);
    try {
      const googleConns = await getGoogleConnections(userId);
      setGoogleConnections(googleConns);

      // Also add to main connections state for UI display
      if (googleConns && googleConns.length > 0) {
        const mappedConnections: Connection[] = googleConns.map((conn) => ({
          id: conn.id,
          platformId: 'google',
          status: 'connected' as const,
          lastSync: new Date(),
          syncFrequency: 3600,
          settings: {
            googleEmail: conn.googleEmail,
            googleName: conn.googleName,
            googlePicture: conn.googlePicture,
          },
          createdAt: conn.createdAt,
          updatedAt: conn.updatedAt,
        }));

        setConnections(prev => {
          const withoutGoogle = prev.filter(c => c.platformId !== 'google');
          return [...withoutGoogle, ...mappedConnections];
        });

        // Get the newest connection (most recently created)
        const newestConn = googleConns.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];

        if (newestConn) {
          // Show setup modal for the newest connection
          setPendingGoogleConnection({
            connectionId: newestConn.id,
            email: newestConn.googleEmail,
          });
          setShowGoogleSetupModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to load Google connections:', error);
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  // Get connection for a platform
  const getConnection = (platformId: string): Connection | undefined => {
    return connections.find((c) => c.platformId === platformId);
  };

  // Filter platforms
  const filteredPlatforms = PLATFORMS.filter((platform) => {
    const matchesCategory = selectedCategory === 'all' || platform.category === selectedCategory;
    const matchesSearch =
      platform.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      platform.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group platforms by category
  const groupedPlatforms = filteredPlatforms.reduce(
    (acc, platform) => {
      if (!acc[platform.category]) {
        acc[platform.category] = [];
      }
      acc[platform.category].push(platform);
      return acc;
    },
    {} as Record<PlatformCategory, Platform[]>
  );

  // Stats
  const connectedCount = connections.filter((c) => c.status === 'connected').length;
  const availableCount = PLATFORMS.filter((p) => !p.comingSoon).length;

  // Demo mode state for Google OAuth simulation
  const [showGoogleAuthDemo, setShowGoogleAuthDemo] = useState(false);
  const [googleAuthStep, setGoogleAuthStep] = useState(0);

  // Trendyol connection modal state
  const [showTrendyolModal, setShowTrendyolModal] = useState(false);

  // Hepsiburada connection modal state
  const [showHepsiburadaModal, setShowHepsiburadaModal] = useState(false);

  // Ciceksepeti connection modal state
  const [showCiceksepetiModal, setShowCiceksepetiModal] = useState(false);
  const [showPttavmModal, setShowPttavmModal] = useState(false);
  const [showN11Modal, setShowN11Modal] = useState(false);
  const [showBeymenModal, setShowBeymenModal] = useState(false);

  // Trendyol settings modal state
  const [showTrendyolSettings, setShowTrendyolSettings] = useState(false);
  const [trendyolSellerId, setTrendyolSellerId] = useState<string>('');

  // Handlers
  const handleConnect = async (platformId: string) => {
    // Check if it's a Google platform - use real OAuth
    if (isGooglePlatform(platformId)) {
      if (!user?.uid) {
        setOauthError(isEnglish ? 'Please login first' : 'Lütfen önce giriş yapın');
        return;
      }

      try {
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        const returnUrl = `${window.location.origin}/dashboard/connections`;

        // Get OAuth URL from backend
        const response = await fetch(
          `${FUNCTIONS_URL}/initiateGoogleAuth?userId=${user.uid}&platformId=${platformId}&returnUrl=${encodeURIComponent(returnUrl)}`
        );

        if (!response.ok) {
          throw new Error('Failed to initiate Google auth');
        }

        const data = await response.json();

        if (data.authUrl) {
          // Redirect to Google OAuth
          window.location.href = data.authUrl;
        } else {
          throw new Error('No auth URL received');
        }
      } catch (error) {
        console.error('Google OAuth error:', error);
        setOauthError(isEnglish ? 'Failed to connect to Google' : 'Google bağlantısı başarısız');
        setTimeout(() => setOauthError(null), 5000);
      }

      return;
    }

    // Check if it's Trendyol - show API key modal
    if (platformId === 'trendyol') {
      setShowTrendyolModal(true);
      return;
    }

    // Check if it's Hepsiburada - show API key modal
    if (platformId === 'hepsiburada') {
      setShowHepsiburadaModal(true);
      return;
    }

    // Check if it's Ciceksepeti - show API key modal
    if (platformId === 'ciceksepeti') {
      setShowCiceksepetiModal(true);
      return;
    }

    // Check if it's PttAVM - show credentials modal
    if (platformId === 'pttavm') {
      setShowPttavmModal(true);
      return;
    }

    // Check if it's N11 - show credentials modal
    if (platformId === 'n11') {
      setShowN11Modal(true);
      return;
    }

    // Check if it's Beymen - show credentials modal
    if (platformId === 'beymen') {
      setShowBeymenModal(true);
      return;
    }

    // For other platforms, use mock connection (TODO: implement generic API key modal)
    console.log('Connect to:', platformId);
    const newConnection: Connection = {
      id: Date.now().toString(),
      platformId,
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setConnections((prev) => [...prev, newConnection]);
  };

  // Handle Trendyol connection success
  const handleTrendyolConnect = (credentials: TrendyolCredentials, connectionId: string) => {
    console.log('Trendyol connected with Seller ID:', credentials.sellerId, 'Connection ID:', connectionId);

    const newConnection: Connection = {
      id: connectionId,
      platformId: 'trendyol',
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      settings: {
        sellerId: credentials.sellerId,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConnections((prev) => {
      // Remove any existing trendyol connections to avoid duplicates
      const withoutTrendyol = prev.filter(c => c.platformId !== 'trendyol');
      return [...withoutTrendyol, newConnection];
    });
    setOauthSuccess(isEnglish ? 'Successfully connected to Trendyol!' : 'Trendyol basariyla baglandi!');
    setTimeout(() => setOauthSuccess(null), 3000);
  };

  // Handle Hepsiburada connection success
  const handleHepsiburadaConnect = (credentials: HepsiburadaCredentials, connectionId: string) => {
    console.log('Hepsiburada connected with Merchant ID:', credentials.merchantId, 'Connection ID:', connectionId);

    const newConnection: Connection = {
      id: connectionId,
      platformId: 'hepsiburada',
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      merchantId: credentials.merchantId,
      settings: {
        merchantId: credentials.merchantId,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConnections((prev) => {
      // Remove any existing hepsiburada connections to avoid duplicates
      const withoutHepsiburada = prev.filter(c => c.platformId !== 'hepsiburada');
      return [...withoutHepsiburada, newConnection];
    });
    setOauthSuccess(isEnglish ? 'Successfully connected to Hepsiburada!' : 'Hepsiburada basariyla baglandi!');
    setTimeout(() => setOauthSuccess(null), 3000);
  };

  // Handle Ciceksepeti connection success
  const handleCiceksepetiConnect = (credentials: CiceksepetiCredentials, connectionId: string) => {
    console.log('Ciceksepeti connected, Connection ID:', connectionId);

    const newConnection: Connection = {
      id: connectionId,
      platformId: 'ciceksepeti',
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      apiKey: credentials.apiKey,
      settings: {},
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConnections((prev) => {
      // Remove any existing ciceksepeti connections to avoid duplicates
      const withoutCiceksepeti = prev.filter(c => c.platformId !== 'ciceksepeti');
      return [...withoutCiceksepeti, newConnection];
    });
    setOauthSuccess(isEnglish ? 'Successfully connected to Ciceksepeti!' : 'Ciceksepeti basariyla baglandi!');
    setTimeout(() => setOauthSuccess(null), 3000);
  };

  // Handle PttAVM connection success
  const handlePttavmConnect = (credentials: PttAVMCredentials, connectionId: string) => {
    console.log('PttAVM connected with Shop ID:', credentials.shopId, 'Connection ID:', connectionId);

    const newConnection: Connection = {
      id: connectionId,
      platformId: 'pttavm',
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      settings: {
        shopId: credentials.shopId,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConnections((prev) => {
      // Remove any existing pttavm connections to avoid duplicates
      const withoutPttavm = prev.filter(c => c.platformId !== 'pttavm');
      return [...withoutPttavm, newConnection];
    });
    setOauthSuccess(isEnglish ? 'Successfully connected to PttAVM!' : 'PttAVM basariyla baglandi!');
    setTimeout(() => setOauthSuccess(null), 3000);
  };

  // Handle N11 connection success
  const handleN11Connect = (credentials: N11Credentials, connectionId: string) => {
    console.log('N11 connected with App Key:', credentials.appKey, 'Connection ID:', connectionId);

    const newConnection: Connection = {
      id: connectionId,
      platformId: 'n11',
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      metadata: {
        storeName: 'N11 Magaza', // Will be updated after first sync
      },
      settings: {},
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConnections((prev) => {
      // Remove any existing n11 connections to avoid duplicates
      const withoutN11 = prev.filter(c => c.platformId !== 'n11');
      return [...withoutN11, newConnection];
    });
    setOauthSuccess(isEnglish ? 'Successfully connected to N11!' : 'N11 basariyla baglandi!');
    setTimeout(() => setOauthSuccess(null), 3000);
  };

  // Handle Beymen connection success
  const handleBeymenConnect = (credentials: BeymenCredentials, connectionId: string) => {
    console.log('Beymen connected with API Key:', credentials.apiKey, 'Connection ID:', connectionId);

    const newConnection: Connection = {
      id: connectionId,
      platformId: 'beymen',
      status: 'connected',
      lastSync: new Date(),
      syncFrequency: 3600,
      metadata: {
        storeName: 'Beymen Magaza', // Will be updated after first sync
      },
      settings: {},
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConnections((prev) => {
      // Remove any existing beymen connections to avoid duplicates
      const withoutBeymen = prev.filter(c => c.platformId !== 'beymen');
      return [...withoutBeymen, newConnection];
    });
    setOauthSuccess(isEnglish ? 'Successfully connected to Beymen!' : 'Beymen basariyla baglandi!');
    setTimeout(() => setOauthSuccess(null), 3000);
  };

  // Handle property selection from modal
  const handlePropertySelect = (selectedItems: SelectedProperty[]) => {
    console.log('Selected properties:', selectedItems);
    // TODO: Save selected properties to Firestore
    // For now, just close the modal
    setShowPropertyModal(false);
    setSelectedGoogleConnection(null);
  };

  const handleDisconnect = async (connectionId: string) => {
    if (!user?.uid) return;

    // Find the connection to determine its type
    const connection = connections.find(c => c.id === connectionId);
    if (!connection) return;

    try {
      if (connection.platformId === 'google') {
        // Disconnect Google
        await disconnectGoogleAccount(user.uid, connectionId);
        setGoogleConnections(prev => prev.filter(c => c.id !== connectionId));
      } else if (connection.platformId === 'trendyol') {
        // Disconnect Trendyol
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        await fetch(`${FUNCTIONS_URL}/disconnectTrendyol`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, connectionId }),
        });
      } else if (connection.platformId === 'hepsiburada') {
        // Disconnect Hepsiburada
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        await fetch(`${FUNCTIONS_URL}/disconnectHepsiburada`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, connectionId }),
        });
      } else if (connection.platformId === 'ciceksepeti') {
        // Disconnect Ciceksepeti
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        await fetch(`${FUNCTIONS_URL}/disconnectCiceksepeti`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, connectionId }),
        });
      } else if (connection.platformId === 'pttavm') {
        // Disconnect PttAVM
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        await fetch(`${FUNCTIONS_URL}/disconnectPttavm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, connectionId }),
        });
      } else if (connection.platformId === 'n11') {
        // Disconnect N11
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        await fetch(`${FUNCTIONS_URL}/disconnectN11`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, connectionId }),
        });
      } else if (connection.platformId === 'beymen') {
        // Disconnect Beymen
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        await fetch(`${FUNCTIONS_URL}/disconnectBeymen`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, connectionId }),
        });
      }

      // Remove from local state
      setConnections((prev) => prev.filter((c) => c.id !== connectionId));
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const handleSync = (connectionId: string) => {
    setConnections((prev) =>
      prev.map((c) =>
        c.id === connectionId
          ? { ...c, status: 'syncing' as const }
          : c
      )
    );
    // Simulate sync completion
    setTimeout(() => {
      setConnections((prev) =>
        prev.map((c) =>
          c.id === connectionId
            ? { ...c, status: 'connected' as const, lastSync: new Date() }
            : c
        )
      );
    }, 2000);
  };

  const handleSettings = (connectionId: string, settings: { syncFrequency: number; dataTypes: string[]; notifications: boolean }) => {
    setConnections((prev) =>
      prev.map((c) =>
        c.id === connectionId
          ? { ...c, syncFrequency: settings.syncFrequency, updatedAt: new Date() }
          : c
      )
    );
    // In real app, this would save to backend
    console.log('Settings saved:', connectionId, settings);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="space-y-6 overflow-hidden">
      {/* OAuth Success Banner */}
      {oauthSuccess && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400">
          <CheckCircle2 size={20} />
          <span className="text-sm font-medium">{oauthSuccess}</span>
        </div>
      )}

      {/* OAuth Error Banner */}
      {oauthError && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
          <X size={20} />
          <span className="text-sm font-medium">{oauthError}</span>
        </div>
      )}

      {/* AI Insight Banner with Stats */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-accent/10 via-purple-500/10 to-blue-500/10 border border-accent/20 p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-accent/20 flex items-center justify-center flex-shrink-0">
            <Zap size={24} className="text-accent sm:hidden" />
            <Zap size={28} className="text-accent hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-text-primary text-lg sm:text-xl">
              {isEnglish ? 'AI-Powered Analytics' : 'Yapay Zeka Destekli Analitik'}
            </h3>
            <p className="text-sm text-text-secondary mt-1 mb-3">
              {isEnglish
                ? 'Connect your platforms for comprehensive AI-powered insights'
                : 'AI destekli içgörüler için platformlarınızı bağlayın'}
            </p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              <span className="px-2.5 py-1 rounded-lg bg-bg-elevated/80 text-xs text-text-secondary border border-border-default">
                {isEnglish ? 'E-commerce' : 'E-ticaret'}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-bg-elevated/80 text-xs text-text-secondary border border-border-default">
                {isEnglish ? 'Marketplaces' : 'Pazaryeri'}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-bg-elevated/80 text-xs text-text-secondary border border-border-default">
                Google
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-bg-elevated/80 text-xs text-text-secondary border border-border-default">
                {isEnglish ? 'Ads' : 'Reklam'}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-bg-elevated/80 text-xs text-text-secondary border border-border-default">
                {isEnglish ? 'Accounting' : 'Muhasebe'}
              </span>
            </div>
          </div>
          {/* Stats - Side by Side */}
          <div className="flex gap-3 flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-500/15 border border-green-500/30">
              <CheckCircle2 size={20} className="text-green-400" />
              <div className="text-center">
                <div className="text-xl font-bold text-green-400 leading-none">{connectedCount}</div>
                <div className="text-[10px] text-green-400/70">{isEnglish ? 'Connected' : 'Bağlı'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-elevated/50 border border-border-default">
              <Database size={20} className="text-text-tertiary" />
              <div className="text-center">
                <div className="text-xl font-bold text-text-secondary leading-none">{availableCount}</div>
                <div className="text-[10px] text-text-tertiary">{isEnglish ? 'Available' : 'Mevcut'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-3 sm:space-y-4">
        {/* Search Bar - Improved Design */}
        <div className="relative">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-text-tertiary">
            <Search size={18} className="sm:hidden" />
            <Search size={20} className="hidden sm:block" />
          </div>
          <input
            type="text"
            placeholder={isEnglish ? 'Search platforms...' : 'Platform ara...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 rounded-xl bg-bg-elevated border border-border-default focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 text-text-primary placeholder:text-text-tertiary text-sm sm:text-base transition-all"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors p-1 rounded-lg hover:bg-bg-surface"
            >
              <X size={16} className="sm:hidden" />
              <X size={18} className="hidden sm:block" />
            </button>
          )}
        </div>

        {/* Category Filter Pills */}
        <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('all')}
            className={cn(
              'px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0',
              selectedCategory === 'all'
                ? 'bg-accent text-white shadow-lg shadow-accent/25'
                : 'bg-bg-elevated text-text-secondary hover:text-text-primary hover:bg-bg-surface border border-border-default'
            )}
          >
            {isEnglish ? 'All' : 'Tümü'}
            <span className={cn(
              'ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs',
              selectedCategory === 'all' ? 'bg-white/20' : 'bg-bg-surface'
            )}>
              {PLATFORMS.length}
            </span>
          </button>
          {(Object.keys(PLATFORM_CATEGORIES) as PlatformCategory[]).map((category) => {
            const config = PLATFORM_CATEGORIES[category];
            const Icon = categoryIcons[category];
            const count = getPlatformsByCategory(category).length;
            const isSelected = selectedCategory === category;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0',
                  isSelected
                    ? 'bg-accent text-white shadow-lg shadow-accent/25'
                    : 'bg-bg-elevated text-text-secondary hover:text-text-primary hover:bg-bg-surface border border-border-default'
                )}
              >
                <Icon size={14} className="sm:hidden" />
                <Icon size={16} className="hidden sm:block" />
                <span className="hidden sm:inline">{isEnglish ? config.labelEn : config.label}</span>
                <span className="sm:hidden">{isEnglish ? config.labelEn.split(' ')[0] : config.label.split(' ')[0]}</span>
                <span className={cn(
                  'px-1.5 sm:px-2 py-0.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs',
                  isSelected ? 'bg-white/20' : 'bg-bg-surface'
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Google Platform - Always at top */}
      {PLATFORMS.find(p => p.id === 'google') && (
        <div className="mb-6">
          <ConnectionCard
            platform={PLATFORMS.find(p => p.id === 'google')!}
            connection={getConnection('google')}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onSync={handleSync}
            onSettings={handleSettings}
            onConfigure={(connectionId) => {
              const googleConn = googleConnections.find(c => c.id === connectionId);
              if (googleConn) {
                setPendingGoogleConnection({
                  connectionId: googleConn.id,
                  email: googleConn.googleEmail,
                });
                setShowGoogleSetupModal(true);
              }
            }}
            selectedProperties={
              getConnection('google')?.id
                ? googleSelectedProperties[getConnection('google')!.id]
                : null
            }
          />
        </div>
      )}

      {/* Platform Grid */}
      {selectedCategory === 'all' ? (
        // Show grouped by category
        <div className="space-y-6 sm:space-y-8">
          {(Object.keys(PLATFORM_CATEGORIES) as PlatformCategory[]).map((category) => {
            // Filter out Google from category lists (it's shown at top)
            const platforms = groupedPlatforms[category]?.filter(p => p.id !== 'google');
            if (!platforms || platforms.length === 0) return null;

            const config = PLATFORM_CATEGORIES[category];
            const Icon = categoryIcons[category];

            return (
              <div key={category}>
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: config.color + '20' }}
                  >
                    <Icon size={16} className="sm:hidden" style={{ color: config.color }} />
                    <Icon size={20} className="hidden sm:block" style={{ color: config.color }} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-text-primary truncate">
                      {isEnglish ? config.labelEn : config.label}
                    </h2>
                    <p className="text-xs sm:text-sm text-text-tertiary">
                      {platforms.length} {isEnglish ? 'platforms' : 'platform'}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {platforms.map((platform) => (
                    <ConnectionCard
                      key={platform.id}
                      platform={platform}
                      connection={getConnection(platform.id)}
                      onConnect={handleConnect}
                      onDisconnect={handleDisconnect}
                      onSync={handleSync}
                      onSettings={handleSettings}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Show flat list for selected category (excluding Google)
        <div className="space-y-3">
          {filteredPlatforms.filter(p => p.id !== 'google').map((platform) => (
            <ConnectionCard
              key={platform.id}
              platform={platform}
              connection={getConnection(platform.id)}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onSync={handleSync}
              onSettings={handleSettings}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredPlatforms.length === 0 && (
        <div className="text-center py-12 sm:py-16 px-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-bg-elevated flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Search size={24} className="text-text-tertiary sm:hidden" />
            <Search size={32} className="text-text-tertiary hidden sm:block" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-2">
            {isEnglish ? 'No platforms found' : 'Platform bulunamadı'}
          </h3>
          <p className="text-sm sm:text-base text-text-secondary max-w-md mx-auto">
            {isEnglish
              ? 'Try adjusting your search query or selecting a different category'
              : 'Arama sorgunuzu veya kategoriyi değiştirmeyi deneyin'}
          </p>
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="mt-3 sm:mt-4 btn btn-ghost text-sm"
            >
              {isEnglish ? 'Clear Search' : 'Aramayı Temizle'}
            </button>
          )}
        </div>
      )}

      {/* Property Selection Modal */}
      {selectedGoogleConnection && (
        <PropertySelectModal
          isOpen={showPropertyModal}
          onClose={() => {
            setShowPropertyModal(false);
            setSelectedGoogleConnection(null);
          }}
          onSelect={handlePropertySelect}
          platformId={selectedGoogleConnection.platformId}
          userId={user?.uid || ''}
          connectionId={selectedGoogleConnection.connectionId}
          googleEmail={selectedGoogleConnection.email}
        />
      )}

      {/* Trendyol Connection Modal */}
      <TrendyolConnectModal
        isOpen={showTrendyolModal}
        onClose={() => setShowTrendyolModal(false)}
        onSuccess={handleTrendyolConnect}
      />

      {/* Hepsiburada Connection Modal */}
      <HepsiburadaConnectModal
        isOpen={showHepsiburadaModal}
        onClose={() => setShowHepsiburadaModal(false)}
        onSuccess={handleHepsiburadaConnect}
      />

      {/* Ciceksepeti Connection Modal */}
      <CiceksepetiConnectModal
        isOpen={showCiceksepetiModal}
        onClose={() => setShowCiceksepetiModal(false)}
        onSuccess={handleCiceksepetiConnect}
      />

      {/* PttAVM Connection Modal */}
      <PttAVMConnectModal
        isOpen={showPttavmModal}
        onClose={() => setShowPttavmModal(false)}
        onSuccess={handlePttavmConnect}
      />

      {/* N11 Connection Modal */}
      <N11ConnectModal
        isOpen={showN11Modal}
        onClose={() => setShowN11Modal(false)}
        onSuccess={handleN11Connect}
      />

      {/* Beymen Connection Modal */}
      <BeymenConnectModal
        isOpen={showBeymenModal}
        onClose={() => setShowBeymenModal(false)}
        onSuccess={handleBeymenConnect}
      />

      {/* Demo Google OAuth Modal */}
      {showGoogleAuthDemo && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Google Header */}
            <div className="bg-white p-6 border-b">
              <div className="flex items-center justify-center mb-4">
                <svg viewBox="0 0 24 24" className="w-8 h-8">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <h2 className="text-xl font-medium text-gray-800 text-center">
                {isEnglish ? 'Sign in with Google' : 'Google ile Oturum Aç'}
              </h2>
              <p className="text-sm text-gray-500 text-center mt-1">
                Bosphorus Labs
              </p>
            </div>

            {/* Progress Steps */}
            <div className="p-8">
              <div className="space-y-4">
                {/* Step 1: Connecting */}
                <div className={cn(
                  'flex items-center gap-4 p-4 rounded-xl transition-all duration-300',
                  googleAuthStep >= 1 ? 'bg-blue-50' : 'bg-gray-50'
                )}>
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center transition-all',
                    googleAuthStep >= 1 ? 'bg-blue-500' : 'bg-gray-300'
                  )}>
                    {googleAuthStep === 1 ? (
                      <Loader2 size={20} className="text-white animate-spin" />
                    ) : googleAuthStep > 1 ? (
                      <CheckCircle2 size={20} className="text-white" />
                    ) : (
                      <div className="w-3 h-3 rounded-full bg-white" />
                    )}
                  </div>
                  <div>
                    <p className={cn(
                      'font-medium transition-colors',
                      googleAuthStep >= 1 ? 'text-blue-700' : 'text-gray-500'
                    )}>
                      {isEnglish ? 'Connecting to Google...' : 'Google\'a bağlanılıyor...'}
                    </p>
                  </div>
                </div>

                {/* Step 2: Authorizing */}
                <div className={cn(
                  'flex items-center gap-4 p-4 rounded-xl transition-all duration-300',
                  googleAuthStep >= 2 ? 'bg-yellow-50' : 'bg-gray-50'
                )}>
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center transition-all',
                    googleAuthStep >= 2 ? 'bg-yellow-500' : 'bg-gray-300'
                  )}>
                    {googleAuthStep === 2 ? (
                      <Loader2 size={20} className="text-white animate-spin" />
                    ) : googleAuthStep > 2 ? (
                      <CheckCircle2 size={20} className="text-white" />
                    ) : (
                      <div className="w-3 h-3 rounded-full bg-white" />
                    )}
                  </div>
                  <div>
                    <p className={cn(
                      'font-medium transition-colors',
                      googleAuthStep >= 2 ? 'text-yellow-700' : 'text-gray-500'
                    )}>
                      {isEnglish ? 'Authorizing permissions...' : 'İzinler yetkilendiriliyor...'}
                    </p>
                  </div>
                </div>

                {/* Step 3: Success */}
                <div className={cn(
                  'flex items-center gap-4 p-4 rounded-xl transition-all duration-300',
                  googleAuthStep >= 3 ? 'bg-green-50' : 'bg-gray-50'
                )}>
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center transition-all',
                    googleAuthStep >= 3 ? 'bg-green-500' : 'bg-gray-300'
                  )}>
                    {googleAuthStep >= 3 ? (
                      <CheckCircle2 size={20} className="text-white" />
                    ) : (
                      <div className="w-3 h-3 rounded-full bg-white" />
                    )}
                  </div>
                  <div>
                    <p className={cn(
                      'font-medium transition-colors',
                      googleAuthStep >= 3 ? 'text-green-700' : 'text-gray-500'
                    )}>
                      {isEnglish ? 'Successfully connected!' : 'Başarıyla bağlandı!'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Services being connected */}
              {googleAuthStep >= 2 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-3 uppercase tracking-wider">
                    {isEnglish ? 'Connecting services:' : 'Bağlanan servisler:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['Analytics', 'Ads', 'Search Console', 'YouTube', 'Drive', 'Sheets', 'Gmail', 'Calendar'].map((service, i) => (
                      <span
                        key={service}
                        className={cn(
                          'px-2 py-1 text-xs rounded-lg transition-all duration-300',
                          googleAuthStep >= 3 || i < (googleAuthStep === 2 ? 4 : 0)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        )}
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Google Services Setup Modal */}
      {pendingGoogleConnection && user?.uid && (
        <GoogleServicesSetupModal
          isOpen={showGoogleSetupModal}
          onClose={() => {
            setShowGoogleSetupModal(false);
            setPendingGoogleConnection(null);
          }}
          onComplete={() => {
            setShowGoogleSetupModal(false);
            setPendingGoogleConnection(null);
            // Reload connections to reflect saved properties
            loadGoogleConnections(user.uid);
          }}
          userId={user.uid}
          connectionId={pendingGoogleConnection.connectionId}
          googleEmail={pendingGoogleConnection.email}
        />
      )}
    </div>
  );
}
